import { authenticatedFetch } from "@lumi/data";
import { useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

interface ReadUrlResponse {
  readUrl: string;
  expiresAt: string;
}

interface UseImageReadUrlParams {
  submissionId?: string | null; // Optional - for submissions
  titleId?: string | null; // Optional - for titles
  key: string | null;
  mode?: "submission" | "title"; // Which endpoint to use
}

interface UseImageReadUrlResult {
  readUrl: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook for fetching presigned read URLs for images in private R2 bucket
 * Automatically fetches when key changes and refreshes before expiration
 */
export function useImageReadUrl({
  submissionId,
  titleId,
  key,
  mode = "submission",
}: UseImageReadUrlParams): UseImageReadUrlResult {
  const { accessToken, refreshAuth } = useAuth();
  const [readUrl, setReadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchReadUrl = async () => {
    const entityId = titleId || submissionId;
    if (!accessToken || !entityId || !key) {
      setReadUrl(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = mode === "title" ? "/v1/admin/images/read-url" : "/v1/provider/images/read-url";

      console.log("useImageReadUrl: Fetching presigned read URL for key:", key, "via", endpoint);

      const response = await authenticatedFetch<ReadUrlResponse>(
        `${import.meta.env.VITE_API_BASE_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...(titleId ? { titleId } : { submissionId }),
            key,
          }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      console.log("useImageReadUrl: Got presigned read URL, expires at:", response.expiresAt);
      setReadUrl(response.readUrl);

      // Refresh the URL 5 minutes before expiration (URLs expire in 1 hour)
      const expiresAt = new Date(response.expiresAt).getTime();
      const refreshAt = expiresAt - 5 * 60 * 1000; // 5 minutes before expiry
      const timeout = refreshAt - Date.now();

      if (timeout > 0) {
        setTimeout(() => {
          console.log("useImageReadUrl: URL about to expire, refreshing...");
          setRefreshTrigger((prev) => prev + 1);
        }, timeout);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch read URL";
      console.error("useImageReadUrl: Error:", errorMessage);
      setError(errorMessage);
      setReadUrl(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchReadUrl();
  }, [submissionId, titleId, key, accessToken, refreshTrigger, mode]);

  const refetch = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return {
    readUrl,
    loading,
    error,
    refetch,
  };
}
